/**
 * Payment Service
 * Flutter source: lib/services/cloud_functions_service.dart:711-733
 *
 * Domain service for payment-related operations. Currently wraps the
 * Revolut exchange-rate cloud function and remaps the DTO to a domain
 * entity. The caller decides which environment to use (mirroring Flutter,
 * where `PaymentUtils.revolutEnvironment.name` is passed in at the call
 * site); the service stays stateless and does not consult any singletons.
 */
import { cloudFunctionsService as defaultCloudFunctionsService } from '@/data/services/cloudFunctionsService';
import type { CloudFunctionsService } from '@/data/services/cloudFunctionsService';
import type { ExchangeRateEntity } from '@/domain/entities/payment/exchangeRateEntity';
import { ExchangeRateRemapper } from '@/domain/remappers/payment/exchangeRateRemapper';
import type { RevolutEnvironment } from '@/domain/enums/payment/revolutEnvironment';

export class PaymentService {
  constructor(
    private cloudFunctions: CloudFunctionsService = defaultCloudFunctionsService,
  ) {}

  /**
   * Fetch the Revolut exchange rate for a currency pair and return the
   * remapped entity, or null if the cloud function call failed or the
   * lookup returned no rate. The data layer already logs and swallows
   * errors, so this method does not add its own try/catch.
   */
  async getRevolutExchangeRate(
    to: string,
    environment: RevolutEnvironment,
    from: string,
  ): Promise<ExchangeRateEntity | null> {
    const dto = await this.cloudFunctions.getRevolutExchangeRate({ to, from, environment });
    if (!dto) {
      return null;
    }
    return ExchangeRateRemapper.dtoToEntity(dto);
  }
}
